/**
 * Карта разделов и эффективные права пользователя (DEV-1252, ADR-0042).
 *
 * Ничего из этого НЕ пишется в localStorage. Гонка auth.store.initialize(),
 * из-за которой сайдбар успевал отрисоваться по устаревшему снимку прав,
 * исчезает по построению, а не подавляется флагом: единственный источник —
 * ответ сервера в текущей сессии. В localStorage('user') остаётся косметика:
 * идентификатор, почта, имя, аватар и роли для ярлыка.
 */
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { isKnownPermission, type Permission } from '../sections/permissions.generated'
import { isKnownSectionKey, SECTION_KEYS, type SectionKey } from '../sections/registry'
import { sectionsApi } from '../sections/sections.api'

/**
 * legacy — сервер про карту разделов не знает (ручки нет). Гард в этом
 * состоянии пропускает всё, как до реформы: фронт уезжает на прод раньше
 * бэкенда, и полный отказ был бы регрессией на ровном месте.
 *
 * ⚠️ Пустой массив разделов — НЕ legacy, а честный отказ: у пользователя нет
 * ни одной роли, принадлежащей приложению. Различать обязательно.
 */
export type SectionsStatus = 'idle' | 'loading' | 'ready' | 'legacy' | 'error'

export const useSectionsStore = defineStore('sections', () => {
  const keys = ref<SectionKey[]>([])
  const permissions = ref<Set<Permission>>(new Set())
  const status = ref<SectionsStatus>('idle')

  /** Идущий запрос: держит single-flight, чтобы N гардов дали один вызов. */
  let inFlight: Promise<void> | null = null

  const isLegacy = computed(() => status.value === 'legacy')

  /**
   * Ключи для навигации. В legacy-режиме сервер карты не знает и список пуст —
   * рисовать по нему меню значило бы спрятать CRM у всех сразу, поэтому там
   * показываем весь реестр, как до реформы. Гарду для этого хватает hasSection.
   */
  const navigationKeys = computed<SectionKey[]>(() =>
    status.value === 'legacy' ? [...SECTION_KEYS] : keys.value,
  )

  const hasSection = (key: SectionKey): boolean =>
    status.value === 'legacy' || keys.value.includes(key)

  const hasPermission = (permission: Permission): boolean =>
    status.value === 'legacy' || permissions.value.has(permission)

  const setPermissions = (raw: readonly string[] | undefined): void => {
    permissions.value = new Set((raw ?? []).filter(isKnownPermission))
  }

  const load = async (): Promise<void> => {
    status.value = 'loading'

    try {
      const response = await sectionsApi.listMySections('crm')

      // ⚠️ Отсутствие поля sections НЕ означает «ручки нет»: protojson не
      // сериализует пустые repeated, и человек без единой роли получает
      // ровно `200 {}`. Отличить «бэкенд не выкачен» можно только по коду
      // ответа — 404 ниже. Проверено вживую на dev: до этой правки учётка
      // без ролей проходила в дашборд как legacy (DEV-1253).
      keys.value = (response.sections ?? [])
        .slice()
        .sort((a, b) => a.sort_order - b.sort_order)
        .map((section) => section.key)
        .filter(isKnownSectionKey)

      status.value = 'ready'
    } catch (error) {
      // 404 — единственный достоверный признак «бэкенд ручку ещё не выкатил».
      // Страховка выката: фронт уезжает на прод раньше auth, и полный отказ
      // был бы регрессией на ровном месте.
      const notFound =
        typeof error === 'object' &&
        error !== null &&
        (error as { response?: { status?: number } }).response?.status === 404

      keys.value = []
      status.value = notFound ? 'legacy' : 'error'

      if (!notFound) {
        throw error
      }
    }
  }

  /**
   * Гарантирует, что карта загружена. Параллельные вызовы делят один запрос:
   * роутер дёргает это на каждом переходе, и без single-flight холодный старт
   * с несколькими редиректами давал бы столько же запросов.
   */
  const ensureLoaded = async (): Promise<void> => {
    if (status.value === 'ready' || status.value === 'legacy') return

    inFlight ??= load().finally(() => {
      inFlight = null
    })

    return inFlight
  }

  /**
   * После 403 от любого API: права изменились, карту надо перечитать.
   *
   * ⚠️ inFlight сбрасывается обязательно. Иначе 403, прилетевший пока запрос
   * карты в полёте, не отменяет его: ensureLoaded() вернул бы тот же старый
   * промис, тот доехал бы с до-инвалидационными данными и поставил статус
   * ready — карта осталась бы протухшей до следующего 403.
   */
  const invalidate = (): void => {
    inFlight = null
    status.value = 'idle'
    keys.value = []
    permissions.value = new Set()
  }

  return {
    keys,
    navigationKeys,
    permissions,
    status,
    isLegacy,
    hasSection,
    hasPermission,
    setPermissions,
    ensureLoaded,
    invalidate,
  }
})
